// 2025 Danish Riding Federation (DRF) Spring-reglement Rules
// Types and constants for horse jumping course design

export type ClassCode = "LF" | "LE" | "LD" | "LC" | "LB1" | "LB2" | "LA1" | "LA2" | "MB1" | "MB2" | "MA1" | "MA2" | "S1" | "S2" | "S3" | "S4" | "Freestyle";

export type ObstacleType = "vertical" | "oxer" | "triple_bar" | "liverpool" | "open_water" | "combination" | "start" | "finish";

export type AnimalType = "horse" | "pony";

export interface JumpQuota {
  minObstacles: number;
  maxObstacles: number;
  maxEfforts: number;
  minCombos: number;
  maxCombos: number;
  waterMaxCm?: number; // undefined if not allowed
  waterMinCm?: number;
  heightMin: number;
  heightMax: number;
  sværhedsgrad: number;
  mustHave: { 
    oxersAtMax: number; 
    fencesAtMax: number;
  };
}

export const DRF_2025_QUOTAS: Record<ClassCode, JumpQuota> = {
  // E– & D-stævne "starter classes"
  LF: {
    minObstacles: 8,
    maxObstacles: 10,
    maxEfforts: 10,
    minCombos: 0,
    maxCombos: 1,
    heightMin: 60,
    heightMax: 70,
    sværhedsgrad: 0,
    mustHave: { oxersAtMax: 0, fencesAtMax: 0 }
  },
  LE: {
    minObstacles: 8,
    maxObstacles: 10,
    maxEfforts: 10,
    minCombos: 0,
    maxCombos: 1,
    heightMin: 70,
    heightMax: 80,
    sværhedsgrad: 0,
    mustHave: { oxersAtMax: 0, fencesAtMax: 0 }
  },
  LD: {
    minObstacles: 8,
    maxObstacles: 10,
    maxEfforts: 10,
    minCombos: 0,
    maxCombos: 1,
    heightMin: 80,
    heightMax: 90,
    sværhedsgrad: 0,
    mustHave: { oxersAtMax: 0, fencesAtMax: 0 }
  },
  
  // Main competition classes
  LC: {
    minObstacles: 8,
    maxObstacles: 10,
    maxEfforts: 10,
    minCombos: 0,
    maxCombos: 1,
    heightMin: 90,
    heightMax: 100,
    sværhedsgrad: 0,
    mustHave: { oxersAtMax: 0, fencesAtMax: 0 }
  },
  LB1: {
    minObstacles: 8,
    maxObstacles: 10,
    maxEfforts: 12,
    minCombos: 1,
    maxCombos: 2,
    waterMaxCm: 325,
    heightMin: 100,
    heightMax: 110,
    sværhedsgrad: 1,
    mustHave: { oxersAtMax: 3, fencesAtMax: 6 }
  },
  LB2: {
    minObstacles: 8,
    maxObstacles: 10,
    maxEfforts: 12,
    minCombos: 1,
    maxCombos: 2,
    waterMaxCm: 325,
    heightMin: 100,
    heightMax: 110,
    sværhedsgrad: 1,
    mustHave: { oxersAtMax: 3, fencesAtMax: 6 }
  },
  LA1: {
    minObstacles: 10,
    maxObstacles: 12,
    maxEfforts: 14,
    minCombos: 1,
    maxCombos: 2,
    waterMaxCm: 325,
    heightMin: 110,
    heightMax: 120,
    sværhedsgrad: 2,
    mustHave: { oxersAtMax: 3, fencesAtMax: 6 }
  },
  LA2: {
    minObstacles: 10,
    maxObstacles: 12,
    maxEfforts: 14,
    minCombos: 1,
    maxCombos: 2,
    waterMaxCm: 325,
    heightMin: 110,
    heightMax: 120,
    sværhedsgrad: 2,
    mustHave: { oxersAtMax: 3, fencesAtMax: 6 }
  },
  MB1: {
    minObstacles: 10,
    maxObstacles: 12,
    maxEfforts: 15,
    minCombos: 2,
    maxCombos: 3,
    waterMaxCm: 350,
    waterMinCm: 300,
    heightMin: 120,
    heightMax: 130,
    sværhedsgrad: 3,
    mustHave: { oxersAtMax: 3, fencesAtMax: 6 }
  },
  MB2: {
    minObstacles: 10,
    maxObstacles: 12,
    maxEfforts: 15,
    minCombos: 2,
    maxCombos: 3,
    waterMaxCm: 350,
    waterMinCm: 300,
    heightMin: 120,
    heightMax: 130,
    sværhedsgrad: 3,
    mustHave: { oxersAtMax: 3, fencesAtMax: 6 }
  },
  MA1: {
    minObstacles: 10,
    maxObstacles: 12,
    maxEfforts: 15,
    minCombos: 2,
    maxCombos: 3,
    waterMaxCm: 400,
    waterMinCm: 350,
    heightMin: 130,
    heightMax: 140,
    sværhedsgrad: 4,
    mustHave: { oxersAtMax: 3, fencesAtMax: 6 }
  },
  MA2: {
    minObstacles: 10,
    maxObstacles: 12,
    maxEfforts: 15,
    minCombos: 2,
    maxCombos: 3,
    waterMaxCm: 400,
    waterMinCm: 350,
    heightMin: 130,
    heightMax: 140,
    sværhedsgrad: 4,
    mustHave: { oxersAtMax: 3, fencesAtMax: 6 }
  },
  S1: {
    minObstacles: 12,
    maxObstacles: 14,
    maxEfforts: 16,
    minCombos: 2,
    maxCombos: 3,
    waterMaxCm: 400,
    waterMinCm: 350,
    heightMin: 135,
    heightMax: 145,
    sværhedsgrad: 5,
    mustHave: { oxersAtMax: 3, fencesAtMax: 6 }
  },
  S2: {
    minObstacles: 12,
    maxObstacles: 14,
    maxEfforts: 18,
    minCombos: 2,
    maxCombos: 3,
    waterMaxCm: 450,
    waterMinCm: 350,
    heightMin: 140,
    heightMax: 150,
    sværhedsgrad: 6,
    mustHave: { oxersAtMax: 3, fencesAtMax: 6 }
  },
  S3: {
    minObstacles: 12,
    maxObstacles: 14,
    maxEfforts: 18,
    minCombos: 2,
    maxCombos: 3,
    waterMaxCm: 450,
    waterMinCm: 350,
    heightMin: 145,
    heightMax: 155,
    sværhedsgrad: 6,
    mustHave: { oxersAtMax: 3, fencesAtMax: 6 }
  },
  S4: {
    minObstacles: 12,
    maxObstacles: 14,
    maxEfforts: 18,
    minCombos: 2,
    maxCombos: 3,
    waterMaxCm: 450,
    waterMinCm: 350,
    heightMin: 150,
    heightMax: 160,
    sværhedsgrad: 6,
    mustHave: { oxersAtMax: 3, fencesAtMax: 6 }
  },
  Freestyle: {
    minObstacles: 0,
    maxObstacles: 99,
    maxEfforts: 99,
    minCombos: 0,
    maxCombos: 99,
    waterMaxCm: 500,
    waterMinCm: 250,
    heightMin: 50,
    heightMax: 180,
    sværhedsgrad: 0,
    mustHave: { oxersAtMax: 0, fencesAtMax: 0 }
  }
};

// Default obstacle distribution percentages
export const DEFAULT_OBSTACLE_DISTRIBUTION = {
  vertical: 45,    // % of total obstacles
  oxer: 40,
  triple_bar: 5,
  liverpool: 5,    // becomes 0 in classes <= LD
  open_water: 5    // only in MB and up, and only outdoors
};

// Check if special obstacles are allowed for a class
export function isSpecialObstacleAllowed(classCode: ClassCode, obstacleType: ObstacleType): boolean {
  // Start and finish markers are always allowed in all classes
  if (obstacleType === 'start' || obstacleType === 'finish') {
    return true;
  }
  
  // Freestyle allows everything
  if (classCode === 'Freestyle') {
    return true;
  }
  
  const quota = DRF_2025_QUOTAS[classCode];
  
  switch (obstacleType) {
    case 'liverpool':
      // Liverpool allowed from LB1 and above
      return ['LB1', 'LB2', 'LA1', 'LA2', 'MB1', 'MB2', 'MA1', 'MA2', 'S1', 'S2', 'S3', 'S4'].includes(classCode);
    case 'open_water':
      // Open water allowed from LB1 and above, only if there's a water quota
      return quota.waterMaxCm !== undefined && ['LB1', 'LB2', 'LA1', 'LA2', 'MB1', 'MB2', 'MA1', 'MA2', 'S1', 'S2', 'S3', 'S4'].includes(classCode);
    default:
      return true;
  }
}

export interface Obstacle {
  id: string;
  type: ObstacleType;
  height: number;
  width?: number; // for oxers and water jumps
  x: number;
  y: number;
  rotation: number; // 0, 90, 180, 270 degrees
  isPartOfCombination?: boolean;
  combinationId?: string;
  number?: number; // sequence number for jumps (not for start/finish)
}

export interface CourseConfiguration {
  classCode: ClassCode;
  arenaWidth: number;
  arenaLength: number;
  isIndoor: boolean;
  animalType: AnimalType;
  obstacles: Obstacle[];
} 